import { getGeneratedTaxonomy } from './taxonomy.generated';
import {
    applyTaxonomyDisplayOverlay,
    applyTaxonomyHierarchyOverlay,
    type TaxonomyDisplayRow,
    type TaxonomyDomainRow,
    type TaxonomyOverlayDiagnostic,
    type TaxonomySourceCatalog,
    type TaxonomyTranslationCatalog,
} from './taxonomyOverlay';

export * from './taxonomyOverlay';

const emittedDiagnostics = new Set<string>();

function reportTaxonomyFallback(value: TaxonomyOverlayDiagnostic) {
    const identity = `${value.code}:${value.locale}:${value.key}`;
    if (emittedDiagnostics.has(identity)) return;
    emittedDiagnostics.add(identity);
    console.warn(
        `[i18n] Taxonomy display fallback (${value.code}) for ${value.key} in ${value.locale}`
    );
}

/** A locale must be present in the target-specific generated source graph. */
export function assertRuntimeTaxonomyLocale(locale: string): void {
    runtimeTaxonomy(locale);
}

function runtimeTaxonomy(locale: string) {
    const generated = getGeneratedTaxonomy(locale) ?? getGeneratedTaxonomy('en');
    if (!generated) {
        throw new Error(`Taxonomy runtime locale ${locale} is unavailable`);
    }
    return {
        locale: generated.overlayLocale,
        source: generated.source as TaxonomySourceCatalog,
        translations: generated.translations as TaxonomyTranslationCatalog | undefined,
    };
}

export function overlayRuntimeTaxonomyRow<T extends TaxonomyDisplayRow>(
    row: T,
    kind: 'domain' | 'field' | 'subfield',
    locale = 'en'
): T {
    const taxonomy = runtimeTaxonomy(locale);
    return applyTaxonomyDisplayOverlay(row, kind, taxonomy.source, {
        locale: taxonomy.locale,
        translations: taxonomy.translations,
        onDiagnostic: reportTaxonomyFallback,
    });
}

export function overlayRuntimeTaxonomyHierarchy<T extends TaxonomyDomainRow>(
    domains: readonly T[],
    locale = 'en'
): T[] {
    const taxonomy = runtimeTaxonomy(locale);
    return applyTaxonomyHierarchyOverlay(domains, taxonomy.source, {
        locale: taxonomy.locale,
        translations: taxonomy.translations,
        onDiagnostic: reportTaxonomyFallback,
    });
}
